import React, { useState, useEffect, useCallback } from 'react';
import { Select, Spin, Empty, Tooltip, Button } from 'antd';
import {
    ReloadOutlined, DownloadOutlined, PrinterOutlined,
    ArrowDownOutlined, ArrowUpOutlined, PercentageOutlined, TrophyOutlined,
    BarChartOutlined, DollarOutlined,
} from '@ant-design/icons';
import { XLSX } from '../../utils/xlsxShim';
import Layout from '../../components/Layout';
import KpiCards from '../../components/KpiCards';
import PageHeader from '../../components/PageHeader';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';
import logoLocal from '../../assets/images/logo.png';

// ── Tokens ─────────────────────────────────────────────────────────────────────
const G = '#1a6b3c';
const G2 = '#134f2d';
const INK = '#0f172a';
const MUTED = '#94a3b8';
const SURF = '#f8fafc';
const BRD = '#e2e8f0';
const WHITE = '#ffffff';

// Palette barres encaissement (vert)
const ENC_F = '#1a6b3c';   // face front
const ENC_T = '#4ade80';   // face top
const ENC_S = '#0f4023';   // face side

// Palette barres paiement (ambre)
const PAI_F = '#d97706';
const PAI_T = '#fbbf24';
const PAI_S = '#7c2d12';

const ANNEE_COURANTE = new Date().getFullYear();
const ANNEES = Array.from({ length: 10 }, (_, i) => ANNEE_COURANTE - 9 + i);

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (v) =>
    v == null ? '-' : new Intl.NumberFormat(localeIntl(), { maximumFractionDigits: 0 }).format(Number(v));

const fmtMt = (v) => {
    const n = Number(v ?? 0);
    // Virgule décimale en français, point en anglais : les deux formes sont passées.
    const abrege = (x, unite) => i18n.t(`statistiques:graphique.abreviations.${unite}`, {
        n: x.toFixed(1).replace('.', ','), nPoint: x.toFixed(1),
    });
    if (n >= 1_000_000_000) return abrege(n / 1_000_000_000, 'milliard');
    if (n >= 1_000_000) return abrege(n / 1_000_000, 'million');
    if (n >= 1_000) return abrege(n / 1_000, 'millier');
    return fmt(n);
};

// ── Barre 3D (face front + top + side) ────────────────────────────────────────
function Bar3D({ x, y, w, h, cf, ct, cs, dimmed, onMouseEnter }) {
    const d = Math.max(w * 0.42, 10);   // profondeur
    const t = d * 0.48;                  // tilt vertical
    if (h <= 0) return null;
    return (
        <g opacity={dimmed ? 0.38 : 1}
            style={{ cursor: 'pointer', transition: 'opacity 0.16s' }}
            onMouseEnter={onMouseEnter}>
            {/* Face frontale */}
            <rect x={x} y={y} width={w} height={h} fill={cf} />
            {/* Face supérieure */}
            <polygon
                points={`${x},${y} ${x + d},${y - t} ${x + w + d},${y - t} ${x + w},${y}`}
                fill={ct}
            />
            {/* Face latérale droite */}
            <polygon
                points={`${x + w},${y} ${x + w + d},${y - t} ${x + w + d},${y + h - t} ${x + w},${y + h}`}
                fill={cs}
            />
            {/* Reflet sur le front */}
            <rect x={x} y={y} width={w * 0.28} height={h} fill="rgba(255,255,255,0.08)" pointerEvents="none" />
        </g>
    );
}

// ── Mini barre 3D pour la légende ──────────────────────────────────────────────
function LegBar({ x, y, cf, ct, cs }) {
    const w = 12, h = 10, d = 5, t = 2.5;
    return (
        <g>
            <rect x={x} y={y} width={w} height={h} fill={cf} />
            <polygon points={`${x},${y} ${x + d},${y - t} ${x + w + d},${y - t} ${x + w},${y}`} fill={ct} />
            <polygon points={`${x + w},${y} ${x + w + d},${y - t} ${x + w + d},${y + h - t} ${x + w},${y + h}`} fill={cs} />
        </g>
    );
}

// ── Graphique barres 3D collées ────────────────────────────────────────────────
function BarChart({ series, mode }) {
    const { t } = useTranslation('statistiques');
    const [tooltip, setTooltip] = useState(null);
    const [hoverYear, setHoverYear] = useState(null);

    if (!series?.length) return <Empty description={t('commun.aucuneDonnee')} style={{ padding: 60 }} />;

    const W = 920;
    const H = 420;
    const PAD = { top: 52, right: 70, bottom: 62, left: 88 };
    const IW = W - PAD.left - PAD.right;
    const IH = H - PAD.top - PAD.bottom;

    const n = series.length;
    const slot = IW / n;
    const barW = Math.min(slot * 0.42, 48);
    const DEPTH = Math.max(barW * 0.40, 10);

    const vals = series.map(d => ({
        year: d.annee,
        enc: mode === 'nb' ? (d.nbEncaissements || 0) : Number(d.montantEncaissements || 0),
        pai: mode === 'nb' ? (d.nbPaiements || 0) : Number(d.montantPaiements || 0),
    }));

    const maxVal = Math.max(...vals.flatMap(v => [v.enc, v.pai]), 1);
    const nTick = 5;
    const raw = maxVal / nTick;
    const mag = Math.pow(10, Math.floor(Math.log10(raw || 1)));
    const step = Math.ceil(raw / mag) * mag || 1;
    const yMax = step * nTick;
    const ticks = Array.from({ length: nTick + 1 }, (_, i) => i * step);

    const yOf = v => PAD.top + IH - (v / yMax) * IH;
    const baseY = PAD.top + IH;

    return (
        <div style={{ position: 'relative', width: '100%', overflowX: 'auto' }}>
            <svg
                viewBox={`0 0 ${W} ${H}`}
                style={{ width: '100%', maxWidth: W, display: 'block', margin: '0 auto' }}
                onMouseLeave={() => { setTooltip(null); setHoverYear(null); }}
            >
                <defs>
                    <linearGradient id="gBg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f0fdf4" stopOpacity="0.6" />
                        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                    </linearGradient>
                    <filter id="sh">
                        <feDropShadow dx="1" dy="3" stdDeviation="3" floodOpacity="0.10" />
                    </filter>
                </defs>

                {/* Fond zone graphe */}
                <rect x={PAD.left} y={PAD.top} width={IW} height={IH} fill="url(#gBg)" />

                {/* Grille + axe Y */}
                {ticks.map((t, i) => {
                    const y = yOf(t);
                    return (
                        <g key={i}>
                            <line x1={PAD.left} x2={PAD.left + IW} y1={y} y2={y}
                                stroke={i === 0 ? '#334155' : BRD}
                                strokeWidth={i === 0 ? 1.5 : 1}
                                strokeDasharray={i === 0 ? '' : '5 4'} />
                            <text x={PAD.left - 10} y={y + 4}
                                textAnchor="end" fill={MUTED}
                                fontSize={10.5} fontFamily="var(--font-main)">
                                {mode === 'nb' ? fmt(t) : fmtMt(t)}
                            </text>
                        </g>
                    );
                })}

                {/* Barres par année */}
                {vals.map((d, i) => {
                    const cx = PAD.left + slot * i + slot / 2;
                    // Les 2 barres sont collées : enc à gauche, pai à droite
                    const xEnc = cx - barW;      // barre enc = barW à gauche du centre
                    const xPai = cx;             // barre pai = barW à droite du centre (collée)
                    const hEnc = Math.max((d.enc / yMax) * IH, d.enc > 0 ? 2 : 0);
                    const hPai = Math.max((d.pai / yMax) * IH, d.pai > 0 ? 2 : 0);
                    const yEnc = yOf(d.enc);
                    const yPai = yOf(d.pai);
                    const dim = hoverYear !== null && hoverYear !== d.year;
                    const tilt = DEPTH * 0.48;

                    return (
                        <g key={d.year} filter="url(#sh)">

                            {/* Zone hover invisible sur toute la hauteur */}
                            <rect
                                x={xEnc - 4} y={PAD.top}
                                width={barW * 2 + DEPTH + 8} height={IH}
                                fill="transparent"
                                onMouseEnter={e => {
                                    setHoverYear(d.year);
                                    setTooltip({ year: d.year, enc: d.enc, pai: d.pai, x: e.clientX, y: e.clientY });
                                }}
                                onMouseMove={e => setTooltip(tt => tt ? { ...tt, x: e.clientX, y: e.clientY } : null)}
                            />

                            {/* Barre Encaissement 3D */}
                            <Bar3D x={xEnc} y={yEnc} w={barW} h={hEnc}
                                cf={ENC_F} ct={ENC_T} cs={ENC_S}
                                dimmed={dim}
                                onMouseEnter={e => {
                                    setHoverYear(d.year);
                                    setTooltip({ year: d.year, enc: d.enc, pai: d.pai, x: e.clientX, y: e.clientY });
                                }}
                            />

                            {/* Barre Paiement 3D · collée à droite */}
                            <Bar3D x={xPai} y={yPai} w={barW} h={hPai}
                                cf={PAI_F} ct={PAI_T} cs={PAI_S}
                                dimmed={dim}
                                onMouseEnter={e => {
                                    setHoverYear(d.year);
                                    setTooltip({ year: d.year, enc: d.enc, pai: d.pai, x: e.clientX, y: e.clientY });
                                }}
                            />

                            {/* Valeur enc au-dessus de la barre */}
                            {hEnc > 20 && (
                                <text x={xEnc + barW / 2} y={yEnc - tilt - 6}
                                    textAnchor="middle" fill={G2}
                                    fontSize={9} fontWeight={700} fontFamily="var(--font-main)">
                                    {mode === 'nb' ? fmt(d.enc) : fmtMt(d.enc)}
                                </text>
                            )}
                            {/* Valeur pai au-dessus */}
                            {hPai > 20 && (
                                <text x={xPai + barW / 2} y={yPai - tilt - 6}
                                    textAnchor="middle" fill={PAI_S}
                                    fontSize={9} fontWeight={700} fontFamily="var(--font-main)">
                                    {mode === 'nb' ? fmt(d.pai) : fmtMt(d.pai)}
                                </text>
                            )}

                            {/* Ligne de base */}
                            <line x1={xEnc} x2={xPai + barW + DEPTH}
                                y1={baseY} y2={baseY}
                                stroke="#334155" strokeWidth={1.5} />

                            {/* Label année */}
                            <text x={cx + DEPTH / 2} y={baseY + 22}
                                textAnchor="middle"
                                fill={hoverYear === d.year ? G : INK}
                                fontSize={12.5} fontWeight={700}
                                fontFamily="DM Sans, sans-serif">
                                {d.year}
                            </text>
                        </g>
                    );
                })}

                {/* Légende */}
                <g transform={`translate(${PAD.left}, ${H - 14})`}>
                    <LegBar x={0} y={-10} cf={ENC_F} ct={ENC_T} cs={ENC_S} />
                    <text x={22} y={0} fontSize={12} fill={INK} fontFamily="DM Sans, sans-serif" fontWeight={500}>
                        {t('graphique.encaissements')}
                    </text>
                    <LegBar x={155} y={-10} cf={PAI_F} ct={PAI_T} cs={PAI_S} />
                    <text x={177} y={0} fontSize={12} fill={INK} fontFamily="DM Sans, sans-serif" fontWeight={500}>
                        {t('graphique.paiements')}
                    </text>
                </g>
            </svg>

            {/* Tooltip */}
            {tooltip && (
                <div style={{
                    position: 'fixed', left: tooltip.x + 18, top: tooltip.y - 60,
                    background: INK, color: WHITE, borderRadius: 12, padding: '12px 16px',
                    fontSize: 12, fontFamily: 'DM Sans, sans-serif', pointerEvents: 'none',
                    zIndex: 9999, boxShadow: '0 16px 40px rgba(0,0,0,0.32)',
                    minWidth: 215, border: '1px solid rgba(255,255,255,0.1)',
                }}>
                    <div style={{
                        fontWeight: 800, fontSize: 16, marginBottom: 10,
                        borderBottom: '1px solid rgba(255,255,255,0.12)', paddingBottom: 7
                    }}>
                        {tooltip.year}
                    </div>
                    {[
                        { label: t('graphique.encaissements'), val: tooltip.enc, cf: ENC_F, ct: ENC_T, cs: ENC_S },
                        { label: t('graphique.paiements'), val: tooltip.pai, cf: PAI_F, ct: PAI_T, cs: PAI_S },
                    ].map(({ label, val, cf, ct, cs }) => (
                        <div key={label} style={{
                            display: 'flex', alignItems: 'center',
                            gap: 8, marginBottom: 7
                        }}>
                            <svg width={14} height={12} viewBox="0 0 14 12">
                                <rect x={0} y={2} width={9} height={10} fill={cf} />
                                <polygon points="0,2 4,0 13,0 9,2" fill={ct} />
                                <polygon points="9,2 13,0 13,10 9,12" fill={cs} />
                            </svg>
                            <span style={{ flex: 1, color: 'rgba(255,255,255,0.62)' }}>{label}</span>
                            <span style={{ fontWeight: 700, fontFamily: 'var(--font-main)', fontSize: 13 }}>
                                {mode === 'nb' ? fmt(val) : fmtMt(val) + ' F'}
                            </span>
                        </div>
                    ))}
                    {tooltip.enc > 0 && (
                        <div style={{
                            paddingTop: 7, borderTop: '1px solid rgba(255,255,255,0.1)',
                            color: 'rgba(255,255,255,0.45)', fontSize: 11,
                            display: 'flex', justifyContent: 'space-between'
                        }}>
                            <span>{t('graphique.ratioPaiEnc')}</span>
                            <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.72)' }}>
                                {((tooltip.pai / tooltip.enc) * 100).toFixed(1)} %
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Tableau récapitulatif ──────────────────────────────────────────────────────
function TableauRecap({ series, mode }) {
    const { t } = useTranslation('statistiques');
    if (!series?.length) return null;
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                <thead>
                    <tr>
                        <th style={th(INK)}>{t('graphique.tableau.annee')}</th>
                        <th style={th(ENC_F)}>{t('graphique.tableau.encaissements', { mesure: mode === 'nb' ? t('graphique.tableau.nbDossiers') : t('graphique.tableau.montantFcfa') })}</th>
                        <th style={th(PAI_F)}>{t('graphique.tableau.paiements', { mesure: mode === 'nb' ? t('graphique.tableau.nbDossiers') : t('graphique.tableau.montantFcfa') })}</th>
                        <th style={th('#1e3a5f')}>{t('graphique.tableau.ecart')}</th>
                        <th style={th('#4a1942')}>{t('graphique.ratioPaiEnc')}</th>
                    </tr>
                </thead>
                <tbody>
                    {series.map((d, i) => {
                        const enc = mode === 'nb' ? (d.nbEncaissements || 0) : Number(d.montantEncaissements || 0);
                        const pai = mode === 'nb' ? (d.nbPaiements || 0) : Number(d.montantPaiements || 0);
                        const diff = enc - pai;
                        const ratio = enc > 0 ? ((pai / enc) * 100).toFixed(1) + ' %' : '-';
                        return (
                            <tr key={d.annee} style={{ background: i % 2 === 0 ? WHITE : SURF }}>
                                <td style={td(true)}>{d.annee}</td>
                                <td style={{ ...td(), color: G, fontWeight: 600 }}>{fmt(enc)}</td>
                                <td style={{ ...td(), color: PAI_F, fontWeight: 600 }}>{fmt(pai)}</td>
                                <td style={{ ...td(), color: diff >= 0 ? G : '#dc2626', fontWeight: 600 }}>
                                    {diff >= 0 ? '+' : ''}{fmt(diff)}
                                </td>
                                <td style={{ ...td(), color: MUTED }}>{ratio}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

const th = (bg) => ({
    padding: '10px 16px', textAlign: 'right', fontSize: 11, fontWeight: 700,
    color: WHITE, letterSpacing: 0.4, background: bg,
    border: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap',
});
const td = (left) => ({
    padding: '9px 16px', textAlign: left ? 'left' : 'right',
    border: `1px solid ${BRD}`, fontSize: 13, fontFamily: 'var(--font-main)',
});

// ── Export Excel ───────────────────────────────────────────────────────────────
async function exportExcel(data) {
    if (!data?.series?.length) return;
    const wb = XLSX.utils.book_new();
    const rows = [
        [
            i18n.t('statistiques:graphique.tableau.annee'),
            i18n.t('statistiques:graphique.excel.nbEncaissements'),
            i18n.t('statistiques:graphique.excel.montantEncaissements'),
            i18n.t('statistiques:graphique.excel.nbPaiements'),
            i18n.t('statistiques:graphique.excel.montantPaiements'),
        ],
        ...data.series.map(d => [
            d.annee, d.nbEncaissements, Number(d.montantEncaissements),
            d.nbPaiements, Number(d.montantPaiements),
        ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 8 }, { wch: 18 }, { wch: 28 }, { wch: 15 }, { wch: 26 }];
    XLSX.utils.book_append_sheet(wb, ws, `Enc_Pai_${data.anneeDebut}_${data.anneeFin}`);
    await XLSX.writeFile(wb, `Graphique_EncPai_${data.anneeDebut}-${data.anneeFin}.xlsx`);
    toast.success(i18n.t('statistiques:graphique.excel.genere'));
}

// ══════════════════════════════════════════════════════════════════════════════
//  PAGE PRINCIPALE
// ══════════════════════════════════════════════════════════════════════════════
export default function GraphiqueEncPai() {
    const { t } = useTranslation('statistiques');
    const [anneeDebut, setAnneeDebut] = useState(ANNEE_COURANTE - 5);
    const [anneeFin, setAnneeFin] = useState(ANNEE_COURANTE);
    const [mode, setMode] = useState('nb');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async (debut, fin) => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(
                `/v1/stats/graphique-enc-pai?anneeDebut=${debut}&anneeFin=${fin}`
            );
            setData(res.data?.data);
        } catch {
            toast.error(t('graphique.erreurChargement'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(anneeDebut, anneeFin); }, [load, anneeDebut, anneeFin]);

    const totalEnc = data?.series?.reduce((s, d) => s + (d.nbEncaissements || 0), 0) ?? 0;
    const totalPai = data?.series?.reduce((s, d) => s + (d.nbPaiements || 0), 0) ?? 0;
    const totalEncMt = data?.series?.reduce((s, d) => s + Number(d.montantEncaissements || 0), 0) ?? 0;
    const totalPaiMt = data?.series?.reduce((s, d) => s + Number(d.montantPaiements || 0), 0) ?? 0;
    const ratioPct = totalEnc > 0 ? ((totalPai / totalEnc) * 100).toFixed(1) + ' %' : '-';
    const anneeMaxEnc = data?.series?.reduce((best, d) => {
        const v = d.nbEncaissements || 0;
        return v > best.v ? { annee: d.annee, v } : best;
    }, { annee: '-', v: 0 })?.annee ?? '-';

    return (
        <Layout title={t('graphique.titreOnglet')}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500;600&display=swap');
                .gep * { font-family: 'DM Sans', sans-serif; }
                @media print { .no-print { display: none !important; } }
                .print-only { display: none; }
                @media print { .print-only { display: block; } }
            `}</style>

            <div className="gep" style={{ maxWidth: 1100 }}>

                {/* Logo · visible uniquement à l'impression */}
                <div className="print-only" style={{ marginBottom: 16 }}>
                    <img src={logoLocal} alt="BNCB" style={{ height: 48 }} />
                </div>

                {/* Compte : `data.series` = une ligne par annee dans le tableau
                    recapitulatif ; les KPI au-dessus sont des cumuls. */}
                <PageHeader
                    titre={t('graphique.titre')}
                    icone={<BarChartOutlined />}
                    compte={data ? (data.series || []).length : undefined}
                    unite={t('graphique.unite')}
                    actions={(
                        <div className="no-print"
                            style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>

                            {/* Toggle */}
                            <div style={{
                                display: 'flex', borderRadius: 8, overflow: 'hidden',
                                border: `1px solid ${BRD}`
                            }}>
                                {[{ v: 'nb', label: t('graphique.modeDossiers') }, { v: 'montant', label: t('graphique.modeMontant') }]
                                    .map(({ v, label }) => (
                                        <button key={v} onClick={() => setMode(v)} style={{
                                            padding: '7px 16px', fontSize: 12, fontWeight: 600, border: 'none',
                                            cursor: 'pointer', background: mode === v ? G : WHITE,
                                            color: mode === v ? WHITE : MUTED, transition: 'all 0.15s',
                                        }}>{label}</button>
                                    ))}
                            </div>

                            <Select value={anneeDebut}
                                onChange={v => { if (v <= anneeFin) setAnneeDebut(v); }}
                                style={{ width: 100 }}
                                options={ANNEES.map(a => ({ value: a, label: String(a) }))} />
                            <span style={{ color: MUTED }}>→</span>
                            <Select value={anneeFin}
                                onChange={v => { if (v >= anneeDebut) setAnneeFin(v); }}
                                style={{ width: 100 }}
                                options={ANNEES.map(a => ({ value: a, label: String(a) }))} />

                            <Tooltip title={t('commun.actualiser')}>
                                <Button icon={<ReloadOutlined />}
                                    onClick={() => load(anneeDebut, anneeFin)}
                                    loading={loading} style={{ borderRadius: 8 }} />
                            </Tooltip>
                            <Button icon={<DownloadOutlined />} onClick={() => exportExcel(data)}
                                disabled={!data}
                                style={{ borderRadius: 8, borderColor: G, color: G, fontWeight: 600 }}>
                                {t('graphique.excel.bouton')}
                            </Button>
                            <Button icon={<PrinterOutlined />} onClick={() => window.print()}
                                disabled={!data} style={{ borderRadius: 8, fontWeight: 600 }}>
                                {t('graphique.imprimer')}
                            </Button>
                        </div>
                    )}
                />

                <Spin spinning={loading}>

                    {/* KPIs */}
                    {data && (
                        <KpiCards
                            columns={4}
                            items={[
                                {
                                    label: t('graphique.kpi.totalEncaissements'), value: fmt(totalEnc),
                                    suffix: fmtMt(totalEncMt) + ' FCFA', color: ENC_F,
                                    icon: <ArrowDownOutlined />,
                                },
                                {
                                    label: t('graphique.kpi.totalPaiements'), value: fmt(totalPai),
                                    suffix: fmtMt(totalPaiMt) + ' FCFA', color: PAI_F,
                                    icon: <ArrowUpOutlined />,
                                },
                                {
                                    label: t('graphique.ratioPaiEnc'), value: ratioPct, color: '#6366f1',
                                    icon: <PercentageOutlined />,
                                },
                                {
                                    label: t('graphique.kpi.meilleureAnnee'), value: String(anneeMaxEnc), color: '#0ea5e9',
                                    icon: <TrophyOutlined />,
                                },
                            ]}
                        />
                    )}

                    {/* Graphique */}
                    <div style={{
                        background: WHITE, border: `1px solid ${BRD}`,
                        borderRadius: 16, padding: '24px 24px 16px', marginBottom: 24,
                        boxShadow: '0 2px 16px rgba(0,0,0,0.05)'
                    }}>
                        <div style={{
                            fontSize: 13, fontWeight: 700, color: INK,
                            marginBottom: 20, display: 'flex',
                            justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <span>
                                {mode === 'nb'
                                    ? <><BarChartOutlined /> {t('graphique.titreGraphiqueNombre')}</>
                                    : <><DollarOutlined /> {t('graphique.titreGraphiqueMontant')}</>}
                            </span>
                            <span style={{
                                fontSize: 11, color: MUTED, fontWeight: 400,
                                background: SURF, padding: '3px 10px', borderRadius: 20,
                                border: `1px solid ${BRD}`
                            }}>
                                {anneeDebut} – {anneeFin}
                            </span>
                        </div>
                        {!loading && !data
                            ? <Empty description={t('commun.aucuneDonnee')} style={{ padding: 60 }} />
                            : <BarChart series={data?.series} mode={mode} />
                        }
                    </div>

                    {/* Tableau */}
                    {data?.series?.length > 0 && (
                        <div style={{
                            background: WHITE, border: `1px solid ${BRD}`,
                            borderRadius: 16, overflow: 'hidden',
                            boxShadow: '0 2px 16px rgba(0,0,0,0.05)'
                        }}>
                            <div style={{
                                padding: '14px 20px', borderBottom: `1px solid ${BRD}`,
                                fontWeight: 700, fontSize: 13, color: INK,
                                display: 'flex', justifyContent: 'space-between'
                            }}>
                                <span>{t('graphique.tableauRecapitulatif')}</span>
                                <span style={{ fontSize: 11, color: MUTED, fontWeight: 400 }}>
                                    {mode === 'nb' ? t('graphique.enNombreDossiers') : t('graphique.enMontantFcfa')}
                                </span>
                            </div>
                            <TableauRecap series={data.series} mode={mode} />
                        </div>
                    )}

                </Spin>

                {data && (
                    <div style={{ marginTop: 12, fontSize: 11, color: MUTED, textAlign: 'right' }}>
                        {t('commun.sourcePointGenereLe', { date: new Date().toLocaleDateString(localeIntl()) })}
                    </div>
                )}
            </div>
        </Layout>
    );
}